import { AnimatePresence, motion } from 'framer-motion';
import React, { useRef, useState } from 'react';
import { Service } from '../../../shared/hooks/useServiceSelection';
import { useResponsive } from '../../../shared/hooks/useResponsive';

interface ImmersiveServiceDisplayProps {
  services: Service[];
  selectedServices: Service[];
  onToggleService: (service: Service) => void;
  onNext: () => void;
  formatDuration: (seconds: number) => string;
  currency: { symbol: string };
  hasNextPage?: boolean;
  hasPrevPage?: boolean;
  onNextPage?: () => void;
  onPrevPage?: () => void;
}

function isSelected(service: Service, selectedServices: Service[]): boolean {
  return selectedServices.some(s => s.id === service.id);
}

function cloudinaryFill(url: string, w: number, h: number): string {
  if (!url) return url;
  const idx = url.indexOf('/upload/');
  if (idx === -1) return url;
  return url.slice(0, idx + 8) + `c_fill,g_auto,w_${w},h_${h}/` + url.slice(idx + 8);
}

function isLeftCard(index: number, count: number): boolean {
  if (count === 1) return true;
  if (count === 2) return index === 0;
  if (count === 3) return index !== 2;
  return index % 2 === 0;
}

function getGridStyle(count: number): React.CSSProperties {
  if (count === 1) return { display: 'grid', gridTemplateColumns: '1fr', gridTemplateRows: '1fr', width: '100%', height: '100%' };
  if (count === 2) return { display: 'grid', gridTemplateColumns: '1fr 1fr', gridTemplateRows: '1fr', width: '100%', height: '100%' };
  return { display: 'grid', gridTemplateColumns: '1fr 1fr', gridTemplateRows: '1fr 1fr', width: '100%', height: '100%' };
}

function getCardStyle(index: number, count: number): React.CSSProperties {
  if (count === 3 && index === 0) return { gridColumn: '1 / -1' };
  return {};
}

const pageVariants = {
  enter: (dir: 'next' | 'prev') => ({ opacity: 0, y: dir === 'next' ? 60 : -60 }),
  center: { opacity: 1, y: 0 },
  exit: (dir: 'next' | 'prev') => ({ opacity: 0, y: dir === 'next' ? -60 : 60 }),
};

const ScrollIndicator: React.FC<{ direction: 'up' | 'down' }> = ({ direction }) => (
  <motion.div
    initial={{ opacity: 0, y: direction === 'down' ? 10 : -10 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, y: direction === 'down' ? 10 : -10 }}
    transition={{ duration: 0.4 }}
    style={{
      position: 'absolute',
      [direction === 'down' ? 'bottom' : 'top']: 32,
      left: '50%',
      transform: 'translateX(-50%)',
      zIndex: 40,
      pointerEvents: 'none',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: 8,
    }}
  >
    {direction === 'up' && (
      <p style={{ color: 'rgba(255,255,255,0.35)', fontSize: 10, letterSpacing: '0.18em', textTransform: 'uppercase' }}>scroll</p>
    )}
    <div style={{ width: 1, height: 32, background: 'rgba(255,255,255,0.15)', position: 'relative', overflow: 'hidden', borderRadius: 1 }}>
      <motion.div
        animate={{ y: direction === 'up' ? ['100%', '-100%'] : ['-100%', '100%'] }}
        transition={{ duration: 1.2, repeat: Infinity, ease: 'easeInOut' }}
        style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '50%', background: 'rgba(255,255,255,0.7)', borderRadius: 1 }}
      />
    </div>
    {direction === 'down' && (
      <p style={{ color: 'rgba(255,255,255,0.35)', fontSize: 10, letterSpacing: '0.18em', textTransform: 'uppercase' }}>scroll</p>
    )}
  </motion.div>
);

const ImmersiveServiceDisplay: React.FC<ImmersiveServiceDisplayProps> = ({
  services,
  selectedServices,
  onToggleService,
  onNext,
  formatDuration,
  currency,
  hasNextPage = false,
  hasPrevPage = false,
  onNextPage,
  onPrevPage,
}) => {
  const { isMobile } = useResponsive();
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const [scrollDir, setScrollDir] = useState<'next' | 'prev'>('next');
  const [pageKey, setPageKey] = useState(0);
  const throttleRef = useRef(false);
  const mobileThrottleRef = useRef(false);

  const count = services.length;
  const cardW = count === 1 ? 1920 : 960;
  const cardH = count <= 2 ? 1080 : 540;

  const handleWheel = (e: React.WheelEvent) => {
    if (throttleRef.current) return;
    throttleRef.current = true;
    setTimeout(() => { throttleRef.current = false; }, 900);

    if (e.deltaY > 0 && hasNextPage) {
      setScrollDir('next');
      setPageKey(k => k + 1);
      onNextPage?.();
    } else if (e.deltaY < 0 && hasPrevPage) {
      setScrollDir('prev');
      setPageKey(k => k + 1);
      onPrevPage?.();
    }
  };

  const handleMobileScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    if (mobileThrottleRef.current) return;
    if (hasNextPage && el.scrollTop + el.clientHeight >= el.scrollHeight - 120) {
      mobileThrottleRef.current = true;
      setTimeout(() => { mobileThrottleRef.current = false; }, 1500);
      onNextPage?.();
    } else if (hasPrevPage && el.scrollTop <= 80) {
      mobileThrottleRef.current = true;
      setTimeout(() => { mobileThrottleRef.current = false; }, 1500);
      onPrevPage?.();
    }
  };

  // ── Mobile layout ──────────────────────────────────────────────────────────
  if (isMobile) {
    const mobileCardH = Math.max(200, Math.floor(window.innerHeight / services.length));
    return (
      <div style={{ width: '100%', height: '100vh', position: 'relative', background: '#080808', overflow: 'hidden' }}>
        <style>{`#lp-svc-list::-webkit-scrollbar { display: none; }`}</style>
        <div
          id="lp-svc-list"
          onScroll={handleMobileScroll}
          style={{ height: '100vh', overflowY: 'auto', scrollbarWidth: 'none' }}
        >
          {services.map((service) => {
            const selected = isSelected(service, selectedServices);
            const imgUrl = service.image?.secure_url
              ? cloudinaryFill(service.image.secure_url, 800, 400)
              : undefined;
            return (
              <div
                key={service.id}
                onClick={() => onToggleService(service)}
                style={{ position: 'relative', height: mobileCardH, overflow: 'hidden', borderBottom: '1px solid rgba(255,255,255,0.07)', cursor: 'pointer' }}
              >
                <div style={{ position: 'absolute', inset: 0, backgroundImage: imgUrl ? `url(${imgUrl})` : 'linear-gradient(135deg,#0f0f0f,#1a1a1a)', backgroundSize: 'cover', backgroundPosition: 'center' }} />
                <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to bottom, rgba(0,0,0,0.15) 0%, rgba(0,0,0,0.78) 100%)' }} />
                {selected && <div style={{ position: 'absolute', inset: 0, boxShadow: 'inset 0 0 0 2px rgba(255,255,255,0.55)', zIndex: 2 }} />}
                {selected && (
                  <div style={{ position: 'absolute', top: 12, right: 12, zIndex: 3, width: 24, height: 24, borderRadius: '50%', background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, color: '#fff' }}>✓</div>
                )}
                <div style={{ position: 'absolute', bottom: 14, left: 16, right: 16, zIndex: 2 }}>
                  <p style={{ color: 'rgba(255,255,255,0.42)', fontSize: 10, letterSpacing: '0.14em', textTransform: 'uppercase', marginBottom: 4 }}>
                    {currency?.symbol}{service.price} · {formatDuration(service.duration)}
                  </p>
                  <h3 style={{ color: '#fff', fontSize: 20, fontWeight: 600, letterSpacing: '-0.01em', lineHeight: 1.2 }}>{service.name}</h3>
                </div>
              </div>
            );
          })}
        </div>

        <AnimatePresence>
          {hasPrevPage && <ScrollIndicator direction="up" />}
        </AnimatePresence>
        <AnimatePresence>
          {hasNextPage && <ScrollIndicator direction="down" />}
        </AnimatePresence>

        <AnimatePresence>
          {selectedServices.length > 0 && (
            <motion.button
              initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 16 }}
              onClick={onNext}
              style={{ position: 'absolute', bottom: 16, right: 16, zIndex: 50, display: 'flex', alignItems: 'center', gap: 10, padding: '14px 24px', background: 'rgba(255,255,255,0.12)', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: 2, color: '#fff', fontSize: 13, letterSpacing: '0.14em', textTransform: 'uppercase', cursor: 'pointer' }}
            >
              Continue <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: 11 }}>({selectedServices.length})</span> <span style={{ fontSize: 16, color: 'rgba(255,255,255,0.5)' }}>→</span>
            </motion.button>
          )}
        </AnimatePresence>
      </div>
    );
  }

  // ── Desktop layout ──────────────────────────────────────────────────────────
  return (
    <div
      style={{ width: '100%', height: '100vh', overflow: 'hidden', position: 'relative' }}
      onWheel={handleWheel}
    >
      <AnimatePresence mode="wait" custom={scrollDir}>
        <motion.div
          key={pageKey}
          custom={scrollDir}
          variants={pageVariants}
          initial="enter"
          animate="center"
          exit="exit"
          transition={{ duration: 0.5, ease: [0.23, 1, 0.32, 1] }}
          style={{ width: '100%', height: '100%' }}
        >
          <div style={getGridStyle(count)}>
            {services.map((service, index) => {
              const selected = isSelected(service, selectedServices);
              const hovered = hoveredId === service.id;
              const isLeft = isLeftCard(index, count);
              const imageUrl = service.image?.secure_url
                ? cloudinaryFill(service.image.secure_url, cardW, cardH)
                : undefined;

              const textSide: React.CSSProperties = isLeft
                ? { left: '6%', maxWidth: '42%', textAlign: 'left' }
                : { right: '6%', maxWidth: '42%', textAlign: 'right' };

              const ctaSide: React.CSSProperties = isLeft
                ? { right: '6%', maxWidth: '42%', textAlign: 'right' }
                : { left: '6%', maxWidth: '42%', textAlign: 'left' };

              const ctaArrow = isLeft ? '←' : '→';

              return (
                <motion.div
                  key={service.id}
                  style={{ ...getCardStyle(index, count), position: 'relative', overflow: 'hidden', cursor: 'pointer', height: '100%' }}
                  onClick={() => onToggleService(service)}
                  onHoverStart={() => setHoveredId(service.id)}
                  onHoverEnd={() => setHoveredId(null)}
                >
                  {/* Background — service image or fallback gradient */}
                  <div style={{
                    position: 'absolute', top: 0, right: 0, bottom: 0, left: 0, zIndex: 0,
                    backgroundImage: imageUrl
                      ? `url(${imageUrl})`
                      : 'linear-gradient(135deg, #0f0f0f 0%, #1a1a1a 50%, #111 100%)',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                  }} />

                  {/* Dark overlay */}
                  <div style={{ position: 'absolute', inset: 0, zIndex: 10, pointerEvents: 'none', background: 'rgba(0,0,0,0.5)' }} />

                  {/* Vignette */}
                  <div style={{
                    position: 'absolute', inset: 0, zIndex: 11, pointerEvents: 'none',
                    background: 'radial-gradient(ellipse at center, transparent 30%, rgba(0,0,0,0.5) 100%)',
                  }} />

                  {/* Hover shimmer */}
                  <motion.div
                    style={{ position: 'absolute', inset: 0, zIndex: 12, pointerEvents: 'none', background: 'rgba(255,255,255,0.04)' }}
                    animate={{ opacity: hovered ? 1 : 0 }}
                    transition={{ duration: 0.22 }}
                  />

                  {/* Selected overlay — subtle tint */}
                  <motion.div
                    style={{ position: 'absolute', inset: 0, zIndex: 13, pointerEvents: 'none', background: 'rgba(255,255,255,0.06)' }}
                    animate={{ opacity: selected ? 1 : 0 }}
                    transition={{ duration: 0.3 }}
                  />

                  {/* Selected ring */}
                  <motion.div
                    style={{ position: 'absolute', inset: 0, zIndex: 14, pointerEvents: 'none', boxShadow: 'inset 0 0 0 2px rgba(255,255,255,0.5)' }}
                    animate={{ opacity: selected ? 1 : 0 }}
                    transition={{ duration: 0.3 }}
                  />

                  {/* Selected checkmark badge — top corner on the text side */}
                  <AnimatePresence>
                    {selected && (
                      <motion.div
                        initial={{ opacity: 0, scale: 0.7 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.7 }}
                        style={{
                          position: 'absolute',
                          top: 20,
                          [isLeft ? 'left' : 'right']: 20,
                          zIndex: 30,
                          pointerEvents: 'none',
                          width: 28,
                          height: 28,
                          borderRadius: '50%',
                          background: 'rgba(255,255,255,0.15)',
                          backdropFilter: 'blur(8px)',
                          WebkitBackdropFilter: 'blur(8px)',
                          border: '1px solid rgba(255,255,255,0.3)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: 13,
                          color: '#fff',
                        }}
                      >
                        ✓
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {/* Main info text */}
                  <div style={{ position: 'absolute', top: '50%', transform: 'translateY(-50%)', zIndex: 22, pointerEvents: 'none', ...textSide }}>
                    <motion.div animate={{ x: hovered ? (isLeft ? -5 : 5) : 0 }} transition={{ duration: 0.35, ease: [0.23, 1, 0.32, 1] }}>
                      {/* Price + duration row */}
                      <p style={{
                        color: 'rgba(255,255,255,0.4)',
                        fontSize: 11,
                        letterSpacing: '0.18em',
                        textTransform: 'uppercase',
                        marginBottom: 14,
                        display: 'flex',
                        gap: 12,
                        justifyContent: isLeft ? 'flex-start' : 'flex-end',
                      }}>
                        <span>{currency?.symbol}{service.price}</span>
                        <span style={{ color: 'rgba(255,255,255,0.25)' }}>·</span>
                        <span>{formatDuration(service.duration)}</span>
                      </p>

                      {/* Service name */}
                      <h2 style={{
                        color: '#ffffff',
                        fontSize: 46,
                        fontWeight: 700,
                        letterSpacing: '-0.01em',
                        lineHeight: 1.0,
                        marginBottom: 14,
                        textShadow: '0 2px 16px rgba(0,0,0,0.6)',
                      }}>
                        {service.name}
                      </h2>

                      {/* Description — truncated */}
                      {service.description && (
                        <p style={{
                          color: 'rgba(255,255,255,0.45)',
                          fontSize: 13,
                          lineHeight: 1.6,
                          textShadow: '0 1px 6px rgba(0,0,0,0.5)',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                        }}>
                          {service.description}
                        </p>
                      )}
                    </motion.div>
                  </div>

                  {/* CTA — opposite side */}
                  <div style={{ position: 'absolute', top: '50%', transform: 'translateY(-50%)', zIndex: 22, pointerEvents: 'none', ...ctaSide }}>
                    <motion.div
                      animate={{ opacity: hovered || selected ? 1 : 0.28, x: hovered ? (isLeft ? 5 : -5) : 0 }}
                      transition={{ duration: 0.35, ease: [0.23, 1, 0.32, 1] }}
                    >
                      <p style={{
                        color: selected ? 'rgba(255,255,255,0.9)' : 'rgba(255,255,255,0.7)',
                        fontSize: 14,
                        letterSpacing: '0.12em',
                        textTransform: 'uppercase',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: isLeft ? 'flex-end' : 'flex-start',
                        gap: 10,
                      }}>
                        {!isLeft && <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: 20 }}>{ctaArrow}</span>}
                        {selected ? 'Remove' : 'Select'}
                        {isLeft && <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: 20 }}>{ctaArrow}</span>}
                      </p>
                    </motion.div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </motion.div>
      </AnimatePresence>

      {/* Scroll up indicator */}
      <AnimatePresence>
        {hasPrevPage && <ScrollIndicator direction="up" />}
      </AnimatePresence>

      {/* Scroll down indicator */}
      <AnimatePresence>
        {hasNextPage && <ScrollIndicator direction="down" />}
      </AnimatePresence>

      {/* Continue button — appears when at least one service selected */}
      <AnimatePresence>
        {selectedServices.length > 0 && (
          <motion.button
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 16 }}
            transition={{ duration: 0.35, ease: [0.23, 1, 0.32, 1] }}
            onClick={onNext}
            style={{
              position: 'absolute',
              bottom: 32,
              right: 40,
              zIndex: 50,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: '14px 28px',
              background: 'rgba(255,255,255,0.1)',
              backdropFilter: 'blur(20px)',
              WebkitBackdropFilter: 'blur(20px)',
              border: '1px solid rgba(255,255,255,0.18)',
              borderRadius: 2,
              color: '#fff',
              fontSize: 13,
              letterSpacing: '0.14em',
              textTransform: 'uppercase',
            }}
            whileHover={{ background: 'rgba(255,255,255,0.16)', scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            Continue
            <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: 11 }}>
              ({selectedServices.length})
            </span>
            <span style={{ fontSize: 16, color: 'rgba(255,255,255,0.5)' }}>→</span>
          </motion.button>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ImmersiveServiceDisplay;
